import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  Box,
  Tabs,
  Tab,
  makeStyles,
  Chip,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  VolumeUp as VolumeUpIcon,
} from "@material-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: theme.spacing(1),
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    width: 36,
    height: 36,
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  emptyMessage: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  summaryBox: {
    display: "flex",
    justifyContent: "space-around",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  summaryItem: {
    textAlign: "center",
  },
  summaryIcon: {
    fontSize: 28,
    marginBottom: theme.spacing(0.5),
  },
  summaryCount: {
    fontWeight: "bold",
    fontSize: "1.25rem",
  },
  summaryLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  readIcon: {
    color: "#53bdeb",
  },
  deliveredIcon: {
    color: theme.palette.text.secondary,
  },
  pendingIcon: {
    color: theme.palette.warning.main,
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const MessageReceiptsModal = ({ open, onClose, message, isGroup }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [receipts, setReceipts] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (open && message?.id && isGroup) {
      fetchReceipts();
    }
  }, [open, message?.id]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/messages/${message.id}/receipts`);
      if (data.success) {
        setReceipts(data.data);
      }
    } catch (err) {
      console.error("Erro ao buscar recibos:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    try {
      return format(new Date(timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return "-";
    }
  };

  const getParticipantNumber = (participant) => {
    // Prioridade: participantNumber > extrair do jid
    if (participant.participantNumber) {
      return participant.participantNumber;
    }
    if (participant.participantJid) {
      return participant.participantJid.replace(/@.*/, "");
    }
    return "";
  };

  const renderParticipantList = (participants, timestampField) => {
    if (!participants || participants.length === 0) {
      return (
        <Typography className={classes.emptyMessage}>
          {i18n.t("messageReceiptsModal.noParticipants")}
        </Typography>
      );
    }

    return (
      <List dense>
        {participants.map((p, index) => (
          <React.Fragment key={p.participantJid || index}>
            <ListItem className={classes.listItem}>
              <ListItemAvatar>
                <Avatar className={classes.avatar}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={p.participantName || getParticipantNumber(p)}
                secondary={
                  <span className={classes.timestamp}>
                    {getParticipantNumber(p)}
                    {p[timestampField] && ` • ${formatTimestamp(p[timestampField])}`}
                  </span>
                }
              />
            </ListItem>
            {index < participants.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  // Para mensagens individuais (não grupo)
  const renderIndividualStatus = () => {
    const ack = message?.ack || 0;
    
    let statusText = i18n.t("messageReceiptsModal.status.pending");
    let StatusIcon = ScheduleIcon;
    let iconClass = classes.pendingIcon;
    
    if (ack >= 4) {
      statusText = i18n.t("messageReceiptsModal.status.read");
      StatusIcon = DoneAllIcon;
      iconClass = classes.readIcon;
    } else if (ack === 3) {
      statusText = i18n.t("messageReceiptsModal.status.delivered");
      StatusIcon = DoneAllIcon;
      iconClass = classes.deliveredIcon;
    } else if (ack === 2) {
      statusText = i18n.t("messageReceiptsModal.status.sent");
      StatusIcon = DoneIcon;
      iconClass = classes.deliveredIcon;
    }
    
    return (
      <Box className={classes.summaryBox}>
        <Box className={classes.summaryItem}>
          <StatusIcon className={`${classes.summaryIcon} ${iconClass}`} />
          <Typography className={classes.summaryCount}>{statusText}</Typography>
          <Typography className={classes.summaryLabel}>
            {i18n.t("messageReceiptsModal.statusLabel")}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">{i18n.t("messageOptionsMenu.details")}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : isGroup && receipts ? (
          <>
            {/* Resumo */}
            <Box className={classes.summaryBox}>
              <Box className={classes.summaryItem}>
                <DoneAllIcon className={`${classes.summaryIcon} ${classes.readIcon}`} />
                <Typography className={classes.summaryCount}>
                  {receipts.read?.length || 0}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("messageReceiptsModal.summary.read")}</Typography>
              </Box>
              <Box className={classes.summaryItem}>
                <DoneAllIcon className={`${classes.summaryIcon} ${classes.deliveredIcon}`} />
                <Typography className={classes.summaryCount}>
                  {receipts.delivered?.length || 0}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("messageReceiptsModal.summary.delivered")}</Typography>
              </Box>
              {receipts.played?.length > 0 && (
                <Box className={classes.summaryItem}>
                  <VolumeUpIcon className={`${classes.summaryIcon} ${classes.readIcon}`} />
                  <Typography className={classes.summaryCount}>
                    {receipts.played.length}
                  </Typography>
                  <Typography className={classes.summaryLabel}>{i18n.t("messageReceiptsModal.summary.played")}</Typography>
                </Box>
              )}
              <Box className={classes.summaryItem}>
                <ScheduleIcon className={`${classes.summaryIcon} ${classes.pendingIcon}`} />
                <Typography className={classes.summaryCount}>
                  {receipts.pending?.length || 0}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("messageReceiptsModal.summary.pending")}</Typography>
              </Box>
            </Box>

            {/* Tabs */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab 
                label={i18n.t("messageReceiptsModal.tabs.read", { count: receipts.read?.length || 0 })}
                icon={<DoneAllIcon className={classes.readIcon} />}
              />
              <Tab 
                label={i18n.t("messageReceiptsModal.tabs.delivered", { count: receipts.delivered?.length || 0 })}
                icon={<DoneAllIcon className={classes.deliveredIcon} />}
              />
              {receipts.played?.length > 0 && (
                <Tab 
                  label={i18n.t("messageReceiptsModal.tabs.played", { count: receipts.played.length })}
                  icon={<VolumeUpIcon />}
                />
              )}
            </Tabs>

            <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
              {renderParticipantList(receipts.read, "readAt")}
            </TabPanel>
            <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
              {renderParticipantList(receipts.delivered, "deliveredAt")}
            </TabPanel>
            {receipts.played?.length > 0 && (
              <TabPanel value={tabValue} index={2} className={classes.tabPanel}>
                {renderParticipantList(receipts.played, "playedAt")}
              </TabPanel>
            )}
          </>
        ) : (
          // Para mensagens individuais
          renderIndividualStatus()
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageReceiptsModal;
